import { logUsage } from 'owa-analytics';
import { getBposNavBarData } from 'owa-bpos-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import { parse as parseUrl } from 'url';

const SHAREPOINT_BASE_URLS = [
    'https://microsoft.sharepoint.com',
    'https://microsoft-my.sharepoint.com',
    'https://microsoft.sharepoint-df.com',
    'https://microsoft-my.sharepoint-df.com',
];

// linkUrl must be of type 'any' because the href being passed in is sometimes a string object instead of a string literal.
// This lets us to do the conversion below where we check the type of linkUrl and if it is instanceof String
// and convert from object to string in this case. This is needed for parse from 'url' to not throw.
export default function isSharePointLink(linkUrl: any): boolean {
    if (!linkUrl) {
        logUsage('isSharePointLinkCalledWithoutUrl');
        return false;
    }

    // VSO 55220 - clean up this logging and try-catch after investigation
    if (typeof linkUrl === 'object') {
        // Per - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Grammar_and_types#String_literals
        // it is possible in javascript for a string to be a string object instead of a string literal
        // url.parse (called later) requires a string literal, not an object. So if the url has typeof === 'object'
        // and is instanceof String it means that the url is a string that we want to handle, but the url parser
        // will throw an exception for.
        if (linkUrl instanceof String) {
            linkUrl = linkUrl.toString();
            logUsage('isSharePointLinkCalledWithNonNullStringObject');
        } else {
            // If the object is not a string then we do not want to handle it.
            // We should never hit this in theory because isSharePointLink should
            // only take in an HREF which should always be instanceof string.
            // Adding extra logging just in case.
            logUsage('isSharePointLinkCalledWithNonNullNonStringObject', null, {
                cosmosOnlyData: JSON.stringify({
                    objectProperties: Object.getOwnPropertyNames(linkUrl),
                }),
            });
            return false;
        }
    }

    const bposNavBarData = getBposNavBarData();
    if (!bposNavBarData) {
        trace.info('isSharePointLink:bposNavBarData is null');
        return false;
    }

    if (isSharePointLinkPrefixedWithBaseUrl(linkUrl, bposNavBarData.SPO_MySiteHostUrl, 'mysite')) {
        return true;
    }

    if (isSharePointLinkPrefixedWithBaseUrl(linkUrl, bposNavBarData.SPO_RootSiteUrl, 'rootsite')) {
        return true;
    }

    if (isFeatureEnabled('doc-SharePoint-dogfoodUser')) {
        return SHAREPOINT_BASE_URLS.some(url =>
            isSharePointLinkPrefixedWithBaseUrl(linkUrl, url, 'dogfood')
        );
    }

    return false;
}

function isSharePointLinkPrefixedWithBaseUrl(
    linkUrl: string,
    baseUrl: string,
    source: string
): boolean {
    let linkHostName: string;
    let baseHostName: string;

    // VSO 55220 - clean up this logging and try-catch after investigation
    try {
        linkHostName = parseUrl(linkUrl).hostname;
    } catch {
        logUsage('isSharePointLinkFailedToParseLinkUrl');
        return false;
    }

    try {
        baseHostName = parseUrl(baseUrl).hostname;
    } catch {
        logUsage('isSharePointLinkFailedToParseBaseUrl', { source: source });
        return false;
    }

    return linkHostName === baseHostName && linkUrl !== baseUrl;
}
